import { useContext, useState } from 'react'
import { TermsAndConditions } from '@/app/[locale]/(common)/terms-and-conditions'
import { Button } from '@/app/[locale]/(ui)/button'
import { OrderContext } from '@/app/[locale]/(shop)/order-page'
import { useBasket } from '@/app/[locale]/(basket)/index'
import { Alert } from '@/app/[locale]/(ui)/alert'
import { createOrder } from '@/lib/orders'
import { OrderSchema } from '@/types/orders'
import { isFreightProduct } from '@/lib/freight'
import { flattenParseErrors } from '@/lib/utils'
import { trackOrderCompleted } from '@/lib/tracking'
import { useTranslations } from 'next-intl'
import { useRouter } from '@/i18n/navigation'
import { OrderBlockAlert } from '@/app/[locale]/(common)/order-block-alert'
import { ValidationAlert } from '@/app/[locale]/(common)/validation-alert'

const getSizeTokenFromValue = (value?: string) => {
  const segment = (value || '').split('/').filter(Boolean).pop()?.toLowerCase().trim() || ''
  if (segment === 'tralle') return 'TRALLE'
  if (segment === 'standard') return 'STANDARD'
  if (segment === 'stor') return 'STOR'
  if (segment === 'xxl') return 'XXL'
  return ''
}

const getEquipmentIdForInvoice = (item: any) => {
  const sku = item?.sku || ''
  const baseEquipmentId = sku.split('-')[0] || ''
  const sizeToken =
    getSizeTokenFromValue(item?.sizePath) ||
    getSizeTokenFromValue(item?.productName) ||
    getSizeTokenFromValue(item?.path)

  if (!sizeToken) return baseEquipmentId

  if (baseEquipmentId.toUpperCase().startsWith('ISEKK')) {
    return `ISEKK${sizeToken}`
  }

  const replaced = baseEquipmentId.replace(/(TRALLE|STANDARD|STOR|XXL)$/i, sizeToken)
  return replaced || baseEquipmentId
}

export const InvoicePayment = () => {
  const t = useTranslations()
  const router = useRouter()
  const basket = useBasket()
  const { cart } = basket
  const { setValidationErrors, ...order } = useContext(OrderContext)
  const [isLoading, setisLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const hasOrderBlock = !!order.customer?.hasOrderBlock
  const hasValidationErrors = Object.keys(order.validationErrors).length > 0
  const canSubmit = !isLoading && hasAcceptedTerms

  const onSubmit = async () => {
    setHasError(false)

    const newOrder = OrderSchema.safeParse({
      customerId: order.customer?.id,
      customerName: order.customer?.name,
      contactPersonId: order.contactPerson?.id,
      products: cart
        .filter((item) => !isFreightProduct(item))
        .map((item) => ({
          equipmentId: getEquipmentIdForInvoice(item),
          wasteId: item.sku.split('-')[1],
          serviceDate: order.pickupDate?.toLocaleDateString('en-CA'),
          quantity: item.quantity,
          messageToDriver: order.driverMessage,
          contactPersonFirstName: order.contactPerson?.firstName,
          contactPersonLastName: order.contactPerson?.lastName,
          contactPersonMobileNumber: order.contactPerson?.phoneNumber,
          location: {
            id: order.location?.id,
            name: order.location?.name,
            street: order.address?.street,
            houseNumber: order.address?.houseNumber,
            postalCode: order.address?.postalCode,
            city: order.address?.city,
            country: order.address?.country,
          },
        })),
    })

    if (newOrder.error) return setValidationErrors(flattenParseErrors(newOrder.error))
    setisLoading(true)

    try {
      const { SapOrderId } = await createOrder(newOrder.data)

      const companyOrderSummary = {
        id: SapOrderId,
        total: basket.total,
        cart: cart.map((item) => ({
          orderItem: {
            ...item,
            imageUrl: item.images?.[0]?.url || item.productImage || '',
          },
          product: {
            name: item.productName || item.name || item.sku,
            images: item.productImage
              ? {
                  content: {
                    images: [{ url: item.productImage }],
                  },
                }
              : undefined,
          },
        })),
      }

      localStorage.setItem(`company-order-${SapOrderId}`, JSON.stringify(companyOrderSummary))
      localStorage.setItem('company-order-latest', JSON.stringify(companyOrderSummary))
      trackOrderCompleted({
        cart,
        total: { gross: basket.total.gross ?? 0 },
        customerType: 'business',
        orderType: 'waste',
        origin: 'webshop',
      })
      router.push(`/ordrebekreftelse/${SapOrderId}?emptyBasket&flow=company`)
    } catch {
      setHasError(true)
    }
    setisLoading(false)
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {hasError && <Alert variant="error">{t('Invoice_submit_error')}</Alert>}
      {hasValidationErrors && <ValidationAlert />}
      {hasOrderBlock ? (
        <OrderBlockAlert />
      ) : (
        <>
          <TermsAndConditions checked={hasAcceptedTerms} onChange={setHasAcceptedTerms} />
          <Button variant="primary" onClick={onSubmit} disabled={!canSubmit}>
            {isLoading ? t('Processing_invoice') : t('Submit_invoice_order')}
          </Button>
        </>
      )}
    </div>
  )
}
